import {create} from '@github-ui/dotcom-schema/protobuf'
import {
  type RepoIssuesDashboardLayoutResponse,
  RepoIssuesDashboardLayoutResponseSchema,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import type {IssueStatusSearchOptions} from '@github-ui/shared-filter-providers/providers/issue-status'
import type {User} from '@github-ui/use-user'

import {repoAppBuilder} from '@github-ui/repo-app-builder'

type LegacyIssuesLayoutPayload = {
  has_projects_enabled: boolean
  has_labels_archiving_enabled: boolean
  // The DataRouter app ships no app payload, so the viewer arrives here instead.
  // Null for logged-out viewers, undefined against a dotcom without github/github#444471.
  current_user?: User | null
  current_user_settings?: {
    preferred_emoji_skin_tone?: number | null
    copilot_show_functionality: boolean
  }
  // Server-read seed of the sidebar collapse cookie; null when disabled. Passed
  // as `initialExpanded` so a collapsed sidebar doesn't flash open on SSR.
  sidebar_expanded?: boolean | null
  // Server-evaluated pinned saved views (empty/absent when the feature is off) so
  // data-router leaves render them on first paint without a client flag or fetch.
  pinned_views?: Array<{
    id: number
    name: string
    icon: string
    color: string
    visibility: string
  }>
  pinned_views_enabled?: boolean
  // Index page config mirrored from the Relay index payload; present on the index path.
  best_match_sort_enabled?: boolean
  issue_types_enabled?: boolean
  issue_status_names?: string[] | null
  issue_status_category_keys?: string[] | null
  issue_status_categories?: Record<string, string> | null
  issue_search_type?: string | null
  show_issue_field_pills?: boolean
  viewer_can_create_issues?: boolean
  is_writable?: boolean
  repository_id?: string
  can_bulk_update?: boolean
  actions?: {
    new_issue?: {
      label: string
      url?: string | null
      disabled_reason?: string | null
    }
  }
  compact_mode?: boolean
}

export type IssuesLayoutPayload = RepoIssuesDashboardLayoutResponse

export function layoutCurrentUser(layout: IssuesLayoutPayload | undefined): User | null {
  const currentUser = layout?.currentUser
  if (!currentUser) return null

  return {
    id: currentUser.id,
    login: currentUser.login,
    name: currentUser.name ?? currentUser.login,
    avatarUrl: currentUser.avatarUrl,
    is_emu: currentUser.isEmu,
  }
}

export function layoutIssueStatusSearchOptions(
  layout: IssuesLayoutPayload | undefined,
): IssueStatusSearchOptions | undefined {
  if (!layout) return undefined

  return {
    statuses: layout.issueStatusNames,
    status_categories: layout.issueStatusCategories,
    categories: layout.issueStatusCategoryKeys,
  }
}

export function normalizeLegacyIssuesLayout(legacy: LegacyIssuesLayoutPayload): IssuesLayoutPayload {
  return create(RepoIssuesDashboardLayoutResponseSchema, {
    hasProjectsEnabled: legacy.has_projects_enabled,
    hasLabelsArchivingEnabled: legacy.has_labels_archiving_enabled,
    currentUser: legacy.current_user
      ? {
          id: legacy.current_user.id,
          login: legacy.current_user.login,
          name: legacy.current_user.name ?? undefined,
          avatarUrl: legacy.current_user.avatarUrl,
          isEmu: legacy.current_user.is_emu,
        }
      : undefined,
    currentUserSettings: legacy.current_user_settings
      ? {
          preferredEmojiSkinTone: legacy.current_user_settings.preferred_emoji_skin_tone ?? undefined,
          copilotShowFunctionality: legacy.current_user_settings.copilot_show_functionality,
        }
      : undefined,
    sidebarExpanded: legacy.sidebar_expanded ?? undefined,
    pinnedViews:
      legacy.pinned_views?.map(view => ({
        id: String(view.id),
        name: view.name,
        icon: view.icon,
        color: view.color,
        visibility: view.visibility,
      })) ?? [],
    pinnedViewsEnabled: legacy.pinned_views_enabled ?? false,
    bestMatchSortEnabled: legacy.best_match_sort_enabled ?? false,
    issueTypesEnabled: legacy.issue_types_enabled ?? false,
    issueStatusNames: legacy.issue_status_names ?? [],
    issueStatusCategoryKeys: legacy.issue_status_category_keys ?? [],
    issueStatusCategories: legacy.issue_status_categories ?? {},
    issueSearchType: legacy.issue_search_type ?? undefined,
    showIssueFieldPills: legacy.show_issue_field_pills ?? false,
    viewerCanCreateIssues: legacy.viewer_can_create_issues ?? false,
    isWritable: legacy.is_writable ?? false,
    repositoryId: legacy.repository_id ?? '',
    canBulkUpdate: legacy.can_bulk_update ?? false,
    actions: legacy.actions
      ? {
          newIssue: legacy.actions.new_issue
            ? {
                label: legacy.actions.new_issue.label,
                url: legacy.actions.new_issue.url ?? undefined,
                disabledReason: legacy.actions.new_issue.disabled_reason ?? undefined,
              }
            : undefined,
        }
      : undefined,
    compactMode: legacy.compact_mode ?? false,
  })
}

/**
 * Issues layout route configuration.
 *
 * Defined as a builder-parameterized factory so tests and the repo app can create the
 * route with their respective builders.
 */
export function buildIssuesLayoutRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('issuesLayoutRoute', {
    path: '/:owner/:repo',
    queries: [
      mainQuery<IssuesLayoutPayload>({
        schema: RepoIssuesDashboardLayoutResponseSchema,
        adapt: legacy => normalizeLegacyIssuesLayout(legacy as LegacyIssuesLayoutPayload),
        // fetches shared layout data (e.g. sidebar config) from a dedicated endpoint,
        // cached and reused across all child routes (e.g. labels, milestones...)
        queryDeps: ({pathname}) => {
          // comma in the beginning to omit the empty string result of split before the first slash
          const [, owner, repo] = pathname.split('/')
          return {pathname: `/${owner}/${repo}/issues/_layout`}
        },
        staleTimeForNavigation: 360000,
      }),
    ],
  })
}

/**
 * Repo-bound route config imported by components that read this route's query data and params.
 */
export const issuesLayoutRoute = buildIssuesLayoutRoute(repoAppBuilder)
